/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import { useToasts } from "react-toast-notifications";
import DatePicker from "react-datepicker";
import { PatientsContext } from ".";
import { useParams } from "react-router-dom";
import spinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import fetchHelper from "../../services/fetchHelper";
import { LayoutContext } from "../../components/layout";
import { toISODate } from "../../utils/helpers";
import useFeatureFlag from "../../utils/featureFlag";
import Select from "react-select";

const DeviceForm = ({ device: defaultDevice }) => {
  const { i } = useParams();
  const { addToast } = useToasts();
  const emptyDevice = {
    patient: {
      id: i,
    },
    deviceType: "PACEMAKER",
    manufacturerName: "BIOTRONIK",
    modelName: "",
    serialNumber: "",
    implantDate: null,
    status: "IMPLANTED",
    advisory: false,
    deleteEnabled: true,
    diagnosticCode: null,
  };
  const [edit, setEdit] = useState(!defaultDevice);
  const [device, setDevice] = useState(defaultDevice || emptyDevice);
  const [errors, setErrors] = useState({});
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diagnosticCode, setDiagnosticCode] = useState({});
  const [diagnosticCodeList, setDiagnosticCodeList] = useState([]);
  const { featureCheck } = useFeatureFlag();
  const advisoryList = [
    { displayText: "NO", value: false },
    { displayText: "YES", value: true },
  ];
  const { devices, setDevices } = useContext(PatientsContext);
  const { headerDetails, updateHeaderDetails } = useContext(LayoutContext);
  const removeDisabled = !device.deleteEnabled;
  const oldDevice = defaultDevice || emptyDevice;

  const remove = async (e) => {
    e.preventDefault();
    if (removing) return false;
    setRemoving(true);
    try {
      await fetchHelper.delete("/patientmanagement/rest/devices/" + device.id);
      setDevices({ list: devices.list.filter((e) => e.id != device.id) });
    } catch ({ errors }) {
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
    setRemoving(false);
  };
  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (removing) return false;
    setSaving(true);
    setDevices({
      saving: true,
    });
    try {
      let updatedDevice;
      const auxDevice = { ...device };
      if ("diagnosticCode" in auxDevice) {
        delete auxDevice.diagnosticCode; //remove the diagnosticCode to avoid issues in the endpoint
      }

      if (device.id) {
        updatedDevice = await fetchHelper.put("/patientmanagement/rest/devices/" + device.id, auxDevice);
      } else {
        updatedDevice = await fetchHelper.post("/patientmanagement/rest/devices/", auxDevice);
      }
      if (featureCheck("ave-18-diagnostic-code-by-device-enabled")) {
        updateDiagnosticCodeDevice(updatedDevice, device.diagnosticCode);
        updatedDevice = { ...updatedDevice, diagnosticCode: device.diagnosticCode };
      }
      let isExist = false;
      setDevices({
        list: devices.list.map((item) => {
          if (item.id === updatedDevice.id) {
            isExist = true;
            return updatedDevice;
          }
          return item;
        }),
      });
      if (!isExist) {
        setDevices({
          list: [updatedDevice, ...devices.list],
          openForm: false,
          saving: false,
        });
      }

      // If we update the device that is being displayed in the header then we need
      // to update that data in the layout context
      if (headerDetails && headerDetails.device.id === updatedDevice.id) {
        updateHeaderDetails({ ...headerDetails, device: { ...headerDetails.device, ...updatedDevice } });
      }
    } catch (err) {
      const isString = typeof err === "string";
      let response = err;
      if (isString) response = JSON.parse(err);
      response.errors &&
        response.errors.forEach((item) => {
          addToast(item.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
      setDevices({
        saving: false,
      });
    }
    setEdit(false);
    setSaving(false);
  };

  const validateFields = (e) => {
    const requiredFields = [
      "serialNumber",
      "implantDate",
      "status",
      "manufacturerName",
      "deviceType",
      "modelName",
      "diagnosticCode",
    ];

    if (featureCheck("ave-18-diagnostic-code-by-device-enabled")) {
      if (!("diagnosticCode" in device)) {
        device["diagnosticCode"] = diagnosticCode?.value;
      }
    } else {
      delete requiredFields[requiredFields.length - 1];
    }

    // When some keys such as the implantDate are null doesn't come in the device obj
    // so we need to include it into the object to perform the validation
    const deviceWithAllKeys = Object.assign(
      {
        implantDate: null,
        status: "IMPLANTED",
        modelName: "",
      },
      device
    );

    const fieldsWithErrors = {};
    Object.entries(deviceWithAllKeys).forEach(([field, fieldValue]) => {
      if (requiredFields.includes(field) && !fieldValue) {
        fieldsWithErrors[field] = true;
      }
    });
    const hasErrors = Object.keys(fieldsWithErrors).length;
    setErrors(fieldsWithErrors);

    if (!hasErrors) {
      setEdit(false);
      save(e);
    }
  };

  useEffect(() => {
    if (devices.addSave && !device.id) {
      validateFields();
    }
  }, [devices.addSave]);

  useEffect(() => {
    if (diagnosticCodeList.length > 0) {
      loadDiagnosticCodeForDevice();
    }
  }, [diagnosticCodeList]);

  const onChange = (e) => {
    const element = e.target;
    const key = element.dataset.key;
    let value = element.value;
    setDevice((device) => ({ ...device, [key]: value }));
  };

  const setSelectedDiagnosticCode = (item) => {
    setDevice((device) => ({ ...device, ["diagnosticCode"]: item.value }));
    setDiagnosticCode(item);
  };

  const loadDiagnosticCodes = async () => {
    if (featureCheck("ave-18-diagnostic-code-by-device-enabled") && diagnosticCodeList.length === 0) {
      let diagnosticCodes = await fetchHelper.get({ url: "/patientmanagement/rest/v2/diagnostic-codes/" });
      let items = diagnosticCodes.map(mapDiagnosticCodeToItem);
      setDiagnosticCodeList(items);
    }
  };

  const loadDiagnosticCodeForDevice = async (device = defaultDevice) => {
    if (device?.externalId) {
      let deviceDiagnosticCodes = await fetchHelper.get({
        url: `/patientmanagement/rest/v2/diagnostic-codes/device/${device.externalId}`,
      });
      if (deviceDiagnosticCodes.length > 0) {
        let item = diagnosticCodeList.filter((dc) => dc.value === deviceDiagnosticCodes[0].externalId);
        if (item.length > 0) {
          setSelectedDiagnosticCode(item[0]);
        }
      }
    }
  };

  const mapDiagnosticCodeToItem = (diagnosticCode) => {
    return {
      label: diagnosticCode.diagnosticCodeName,
      value: diagnosticCode.externalId,
    };
  };

  const updateDiagnosticCodeDevice = async (device, diagnosticCodeId) => {
    await fetchHelper.put(`/patientmanagement/rest/devices/${device.id}/diagnosticCode`, {
      diagnosticCodeId: diagnosticCodeId,
    });
  };

  useEffect(() => {
    setErrors({});
    loadDiagnosticCodes();
  }, []);

  return (
    <>
      <div className="row rowStretch location-row" style={{ position: "relative" }}>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Device Type <sub className="required-field">*</sub>
            </span>
            <select value={device.deviceType || "PACEMAKER"} data-key="deviceType" onChange={onChange}>
              {[
                "PACEMAKER",
                "CRTP",
                "ICD",
                "CRTD",
                "ILR",
                "IPG",
                "PPM",
                "MONITOR",
                "ICM",
                "MCT",
                "EVENT",
                "HOLTER",
                "BGM",
                "BP_CUFF",
                "WEIGHTSCALE",
                "OTHER",
              ].map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            {errors.deviceType && (
              <span className="error">Required fields missing data. Please review and re-submit.</span>
            )}
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Manufacturer <sub className="required-field">*</sub>
            </span>
            <select
              value={device.manufacturerName || "BIOTRONIK"}
              data-key="manufacturerName"
              onChange={onChange}
            >
              {["BIOTRONIK", "BOSTONSCI", "MEDTRONIC", "STJUDE", "BIOTEL", "OTHER"].map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            {errors.manufacturerName && (
              <span className="error">Required fields missing data. Please review and re-submit.</span>
            )}
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Device Status <sub className="required-field">*</sub>
            </span>
            <select value={device.status || "IMPLANTED"} data-key="status" onChange={onChange}>
              {["IMPLANTED", "EXPLANTED"].map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            {errors.status && (
              <span className="error">Required fields missing data. Please review and re-submit.</span>
            )}
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Serial Number <sub className="required-field">*</sub>
            </span>
            <input type="text" value={device.serialNumber} data-key="serialNumber" onChange={onChange} />
            {errors.serialNumber && (
              <span className="error">Required fields missing data. Please review and re-submit.</span>
            )}
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Device Model <sub className="required-field">*</sub>
            </span>
            <input type="text" value={device.modelName} data-key="modelName" onChange={onChange} />
            {errors.modelName && (
              <span className="error">Required fields missing data. Please review and re-submit.</span>
            )}
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label className="inCal">
            <span>
              Implant Date <sub className="required-field">*</sub>
            </span>
          </label>
          <DatePicker
            selected={(device?.implantDate && new Date(device?.implantDate.replaceAll("-", "/"))) || ""}
            id="implant-date-device"
            onChange={(date) => {
              setDevice((device) => ({ ...device, ["implantDate"]: toISODate(date) }));
            }}
          />
          {errors.implantDate && (
            <span className="error">Required fields missing data. Please review and re-submit.</span>
          )}
        </div>
        {featureCheck("ave-18-diagnostic-code-by-device-enabled") && (
          <>
            <div id="DiagnosticDivContainer" className="col col-s-50 col-m-50 col33">
              <label>
                <span>
                  Diagnosis Code <sub className="required-field">*</sub>
                </span>
                <Select
                  id="diagnosticCode"
                  inputId="diagnosticCode"
                  name="diagnosticCode"
                  isSearchable
                  options={diagnosticCodeList}
                  classNames={{
                    control: (state) => "diagnosticCodeCombo",
                  }}
                  onChange={setSelectedDiagnosticCode}
                  value={diagnosticCode}
                  placeholder="Search diagnostic Code"
                />
                {errors.diagnosticCode && (
                  <span className="error">Required fields missing data. Please review and re-submit.</span>
                )}
              </label>
            </div>
            <div className="col col-s-100 col-m-50 col33">
              <label>
                <span>Advisory</span>
                <select value={device.advisory} data-key="advisory" onChange={onChange}>
                  {advisoryList.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.displayText}
                    </option>
                  ))}
                </select>
              </label>
              {!edit ||
                (saving && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255,255,255,.25)",
                    }}
                  />
                ))}
            </div>
            <div className="col col-s-100 col-m-50 col33"></div>
          </>
        )}
        {!edit || saving ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255,255,255,.25)",
            }}
          />
        ) : null}
      </div>
      <div className={`row rowStretch location-row ${!removeDisabled && "form-divider"}`}>
        {defaultDevice && (
          <div className="col col-s-100 col-m-50 col33" style={{ display: "flex", marginTop: "24px" }}>
            {!edit ? (
              <button
                className="btn btn-sm"
                style={{ width: "87px" }}
                onClick={(e) => {
                  e.preventDefault();
                  setEdit(true);
                }}
              >
                {saving ? <img id="loading-spinner-1" src={spinner} style={{ width: 15 }} /> : "Edit"}
              </button>
            ) : (
              <button
                className="btn btnTeal btn-sm"
                style={{ width: "87px" }}
                onClick={(e) => {
                  e.preventDefault();
                  validateFields();
                }}
              >
                {saving ? <img id="loading-spinner-2" src={spinner} style={{ width: 15 }} /> : "Save"}
              </button>
            )}
            {!edit ? (
              <button
                className="btn btn-sm btn-urgent"
                style={{ width: "87px" }}
                onClick={remove}
                disabled={!device.deleteEnabled}
              >
                {removing ? <img id="loading-spinner-3" src={spinner} style={{ width: 15 }} /> : "Remove"}
              </button>
            ) : (
              <button
                className="btn btn-sm"
                style={{ width: "87px" }}
                onClick={(e) => {
                  e.preventDefault();
                  setDevice({ ...oldDevice });
                  setEdit(false);
                  setErrors({});
                }}
              >
                Cancel
              </button>
            )}
          </div>
        )}
        <div className="col col-s-100 col-m-50 col33"></div>
        {/* this select can be deleted later... was duplicated higher up due to how the window is designed */}
        {!featureCheck("ave-18-diagnostic-code-by-device-enabled") && (
          <div className="col col-s-100 col-m-50 col33" style={!defaultDevice ? { width: "10px" } : null}>
            <label>
              <span>Advisory</span>
              <select value={device.advisory} data-key="advisory" onChange={onChange}>
                {advisoryList.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.displayText}
                  </option>
                ))}
              </select>
            </label>
            {!edit ||
              (saving && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255,255,255,.25)",
                  }}
                />
              ))}
          </div>
        )}
      </div>
      {!device.deleteEnabled && (
        <div className="row rowStretch location-row form-divider">
          <div className="col col-s-100 col-m-50 col33">
            <span className="error" style={{ marginLeft: "5px" }}>
              Unable to remove the device due to being associated with an active remote monitoring cycle
              and/or interrogation(s)
            </span>
          </div>
        </div>
      )}
    </>
  );
};

const Device = () => {
  const { fetchDeviceInfo, devices } = useContext(PatientsContext);
  /* call the hook to cache the data, as the children are expecting
  pre-loaded data, we will use the isLoadComplete flag
  to render components conditionally once the load is completed.
  */
  const { isLoadCompleted } = useFeatureFlag();

  useEffect(() => {
    fetchDeviceInfo();
  }, []);

  return (
    <div className="tabContent device-information">
      <img id="loading-spinner-4" src={spinner} style={{ width: 0, opacity: 0, height: 0 }} />
      <div className="content">
        <div className="grpHdrPg">
          <h3>
            Patient Profile: <span>John Smith</span>
          </h3>
        </div>
        <div className="section-header">
          <h3>Device Information</h3>
          <span className="message danger-message">* Indicates Required Field</span>
        </div>
        {isLoadCompleted && devices.openForm ? <DeviceForm /> : null}
        {isLoadCompleted &&
          devices.list.map((item) => <DeviceForm key={`device-${item.id}`} device={item} />)}
      </div>
    </div>
  );
};

export default Device;
